/**
 * AccountantViewDialog — cuts AccountsIQ invoice keying time to near-zero.
 *
 * Renders the dispatch as the operator needs it for raising sales invoices
 * in AccountsIQ: aggregated by (Customer-Code, Product, Packaging) with
 * Cases, Units and Net Weight (kg). Two action buttons:
 *
 *   • Copy to Clipboard  → tab-separated rows pastable straight into
 *                          AccountsIQ / Excel / Sheets.
 *   • Print / Save PDF   → opens the OS print dialog scoped to the table
 *                          so the accountant can keep a hard-copy.
 *
 * Aggregation logic mirrors the back-end Dispatch-Note Sales Order
 * Summary (see backend/routes/dispatch_documents.py). When a product
 * spans multiple batches the operator no longer has to add weights by
 * hand.
 */
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Copy, Printer, FileSpreadsheet, Check, Receipt, CircleCheck, AlertCircle, Send } from 'lucide-react';
import { markDispatchInvoiced, getProducts } from '../lib/api';
import { openPrintWindow } from '../lib/printWindow';
import { InvoicePreviewDialog } from './accountsiq/InvoicePreviewDialog';

const fmtN = (n, dp = 0) => {
  const v = Number(n || 0);
  return v.toFixed(dp);
};

/**
 * Build the AccountsIQ aggregation key:
 *   Customer (Supplier) Code + Product ID + Packaging (Co-Brand)
 * Mirrors backend `product_totals` grouping. Falls back to product_name
 * when a customer-code is missing so we still aggregate cleanly.
 */
const lineKey = (line, cobrandLabel) => [
  (line.supplier_product_code || '').trim(),
  line.product_id || line.product_name || '',
  cobrandLabel || '',
].join('||');

const buildRows = (dispatch, productSkuById = {}) => {
  if (!dispatch) return [];
  const cobrandLabel = dispatch.cobranding_name || 'Standard';
  const groups = new Map();
  for (const line of dispatch.lines || []) {
    if (!line) continue;
    // Skip phantom / empty / out-of-stock lines so the totals match the
    // dispatched product exactly.
    if (line.out_of_stock) continue;
    const qty = Number(line.quantity || 0);
    const cases = Number(line.cases ?? line.cases_override ?? line.calculated_cases ?? 0);
    const weight = Number(line.weight_override || line.total_weight || line.estimated_weight || 0);
    if (qty <= 0 && cases <= 0 && weight <= 0) continue;
    const k = lineKey(line, cobrandLabel);
    if (!groups.has(k)) {
      // SKU resolution priority:
      //   1. Value already persisted on the line (newer dispatches)
      //   2. Lookup map keyed by product_id (older dispatches — looked up
      //      from /api/products on dialog open)
      const skuFromLine = line.product_sku || line.sku || '';
      const skuFromLookup = line.product_id ? (productSkuById[line.product_id] || '') : '';
      groups.set(k, {
        customer_code: (line.supplier_product_code || '').trim(),
        product_name: line.product_name || '',
        product_sku: skuFromLine || skuFromLookup,
        packaging: cobrandLabel,
        units: 0, cases: 0, net_weight: 0,
      });
    }
    const g = groups.get(k);
    g.units += qty;
    g.cases += cases;
    g.net_weight += weight;
  }
  return [...groups.values()].sort((a, b) =>
    (a.packaging || '').localeCompare(b.packaging || '') ||
    (a.customer_code || '').localeCompare(b.customer_code || '') ||
    (a.product_name || '').localeCompare(b.product_name || ''),
  );
};

export default function AccountantViewDialog({ open, onOpenChange, dispatch, onInvoiceMarked }) {
  const [copied, setCopied] = useState(false);
  const printRef = useRef(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [localInvoice, setLocalInvoice] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  useEffect(() => { setLocalInvoice(null); }, [dispatch?.id]);
  // SKU lookup: { [product_id]: sku } — loaded once on first open so the SKU
  // column resolves on legacy dispatches that pre-date the product_sku enrichment.
  const [productSkuById, setProductSkuById] = useState({});
  // Use a ref for the "already-loaded" sentinel so the effect's dependency
  // array doesn't re-trigger after the first successful load. Without this,
  // the effect re-runs on every state change while the dialog is open.
  const skuLoadedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    if (skuLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getProducts({ limit: 500 });
        if (cancelled) return;
        // Be paranoid about response shape — different routes return either
        // a flat array, an envelope { items: [...] }, or { products: [...] }.
        // If anything else lands here we just bail with an empty map.
        let items = [];
        const data = res?.data;
        if (Array.isArray(data)) items = data;
        else if (Array.isArray(data?.items)) items = data.items;
        else if (Array.isArray(data?.products)) items = data.products;
        const map = {};
        for (const p of items) {
          // Skip anything missing id or sku (or with non-string sku)
          if (p && p.id && p.sku) {
            map[p.id] = String(p.sku).trim();
          }
        }
        if (!cancelled) {
          setProductSkuById(map);
          skuLoadedRef.current = true;
        }
      } catch (e) {
        // Non-fatal: dialog still works, SKU column just shows '—' for
        // lines without a persisted sku. Mark loaded so we don't retry
        // forever on every state change.
        if (!cancelled) skuLoadedRef.current = true;
        console.warn('[AccountantViewDialog] product SKU lookup failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const rows = useMemo(() => buildRows(dispatch, productSkuById), [dispatch, productSkuById]);
  const totals = useMemo(() => rows.reduce((acc, r) => ({
    units: acc.units + r.units, cases: acc.cases + r.cases, net_weight: acc.net_weight + r.net_weight,
  }), { units: 0, cases: 0, net_weight: 0 }), [rows]);

  // Use the local invoice state if present (just-marked), else the
  // server-side value the dispatch arrived with.
  const invoiceInfo = localInvoice || {
    invoice_number: dispatch?.invoice_number,
    invoice_status: dispatch?.invoice_status,
    invoice_raised_at: dispatch?.invoice_raised_at,
    invoice_raised_by: dispatch?.invoice_raised_by,
  };
  const isInvoiced = invoiceInfo.invoice_status === 'invoiced' && invoiceInfo.invoice_number;

  const handleMarkInvoiced = async () => {
    const inv = invoiceNumber.trim();
    if (inv.length < 1) {
      toast.error('Enter the AccountsIQ invoice number');
      return;
    }
    setSavingInvoice(true);
    try {
      const { data } = await markDispatchInvoiced(dispatch.id, { invoice_number: inv });
      setLocalInvoice(data);
      setInvoiceNumber('');
      toast.success(`Marked invoiced — ${data.invoice_number}`);
      if (typeof onInvoiceMarked === 'function') {
        onInvoiceMarked(dispatch.id, data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark invoiced');
    } finally {
      setSavingInvoice(false);
    }
  };

  const handleAiqSent = (dispatchId, patch) => {
    setLocalInvoice(patch);
    if (typeof onInvoiceMarked === 'function') onInvoiceMarked(dispatchId, patch);
  };

  const copyToClipboard = async () => {
    // Tab-separated so AccountsIQ / Excel / Google Sheets pastes one cell
    // per column. Includes a header row.
    const header = ['Customer Code', 'Product', 'SKU', 'Packaging', 'Units', 'Cases', 'Net kg'].join('\t');
    const body = rows.map((r) => [
      r.customer_code || '',
      r.product_name || '',
      r.product_sku || '',
      r.packaging || '',
      fmtN(r.units, 0),
      fmtN(r.cases, 1),
      fmtN(r.net_weight, 2),
    ].join('\t')).join('\n');
    const footer = ['', 'TOTAL', '', '', fmtN(totals.units, 0), fmtN(totals.cases, 1), fmtN(totals.net_weight, 2)].join('\t');
    const text = [header, body, footer].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${rows.length} invoice line(s) copied — paste into AccountsIQ`);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      // Fallback for very old browsers / non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        toast.success('Invoice lines copied');
      } catch {
        toast.error('Copy failed — please select the table manually');
      } finally {
        ta.remove();
      }
    }
  };

  const printContent = () => {
    // Pop a new window with ONLY the table so the operator can print or
    // save-as-PDF without the surrounding app chrome.
    const safe = (v) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const html = `<!doctype html>
<html><head>
<meta charset="utf-8" />
<title>AccountsIQ Invoice Lines — ${safe(dispatch?.dispatch_number || '')}</title>
<style>
  body { font-family: Helvetica, Arial, sans-serif; padding: 24px; color: #111; }
  h1 { font-size: 16px; margin: 0 0 4px; }
  .meta { font-size: 11px; color: #555; margin-bottom: 14px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
  th { background: #f1f5f9; color: #0d3b5c; font-weight: 700; text-transform: uppercase; font-size: 10px; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  tr.total td { font-weight: 700; background: #f8fafc; border-top: 2px solid #0d3b5c; }
  .signoff { margin-top: 30px; font-size: 11px; color: #444; }
  @media print { .no-print { display: none; } body { padding: 8px; } }
</style></head><body>
<h1>AccountsIQ Invoice Lines</h1>
<div class="meta">
  Dispatch #${safe(dispatch?.dispatch_number || '')} &middot;
  Customer: ${safe(dispatch?.customer_name || '')} &middot;
  PO: ${safe(dispatch?.po_number || '—')} &middot;
  Dispatch Date: ${safe(dispatch?.dispatch_date || '')}
</div>
<table>
  <thead>
    <tr>
      <th>Customer Code</th><th>Product</th><th>SKU</th><th>Packaging</th>
      <th class="num">Units</th><th class="num">Cases</th><th class="num">Net kg</th>
    </tr>
  </thead>
  <tbody>
    ${rows.map((r) => `<tr>
      <td>${safe(r.customer_code)}</td>
      <td>${safe(r.product_name)}</td>
      <td>${safe(r.product_sku)}</td>
      <td>${safe(r.packaging)}</td>
      <td class="num">${fmtN(r.units, 0)}</td>
      <td class="num">${fmtN(r.cases, 1)}</td>
      <td class="num">${fmtN(r.net_weight, 2)}</td>
    </tr>`).join('')}
    <tr class="total">
      <td colspan="4">TOTAL</td>
      <td class="num">${fmtN(totals.units, 0)}</td>
      <td class="num">${fmtN(totals.cases, 1)}</td>
      <td class="num">${fmtN(totals.net_weight, 2)}</td>
    </tr>
  </tbody>
</table>
<div class="signoff">Prepared: ${new Date().toLocaleString('en-IE')} — printed for AccountsIQ invoice generation.</div>
</body></html>`;
    openPrintWindow(html, { features: 'width=900,height=900' });
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="accountant-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-700" />
            AccountsIQ Invoice Lines
          </DialogTitle>
          <DialogDescription className="text-xs">
            Aggregated by Customer Code + Product + Packaging — ready to paste into AccountsIQ.
            Net weight already sums every batch so no manual math is needed.
          </DialogDescription>
        </DialogHeader>

        <div className="text-xs text-muted-foreground mb-2 flex flex-wrap gap-x-4 gap-y-1">
          <span><b>Dispatch:</b> {dispatch?.dispatch_number || '—'}</span>
          <span><b>Customer:</b> {dispatch?.customer_name || '—'}</span>
          <span><b>PO:</b> {dispatch?.po_number || '—'}</span>
          <span><b>Date:</b> {dispatch?.dispatch_date || '—'}</span>
        </div>

        {/* ── Invoice status panel ──
            Shows whether the dispatch has been raised in AccountsIQ.
            "Invoiced" → green chip with the invoice number.
            "Pending" → input + button to record the AccountsIQ invoice
            number against the dispatch in one click. Closes the loop
            described in the dispatch-view-row chip story. */}
        <div className={`rounded-md border p-3 mb-3 ${isInvoiced ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
          {!isInvoiced && (
            <div className="flex items-center justify-between gap-3 flex-wrap pb-3 mb-3 border-b border-amber-200/70">
              <div className="text-sm text-amber-900">
                <div className="font-semibold">Send it straight to AccountsIQ</div>
                <div className="text-xs text-amber-800/80">Review shipped vs. invoiced quantities and AccountsIQ prices, then create the draft invoice.</div>
              </div>
              <Button onClick={() => setPreviewOpen(true)} className="bg-[#005F73] hover:bg-[#0A9396]" data-testid="btn-aiq-preview-from-accountant">
                <Send className="w-4 h-4 mr-1.5" /> Preview &amp; send to AccountsIQ
              </Button>
            </div>
          )}
          {isInvoiced ? (
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-emerald-900">
                <CircleCheck className="w-5 h-5 text-emerald-600" />
                <div>
                  <div className="font-semibold">AccountsIQ Invoiced: {invoiceInfo.invoice_number}</div>
                  <div className="text-xs text-emerald-800/80">
                    Raised by {invoiceInfo.invoice_raised_by || '—'}
                    {invoiceInfo.invoice_raised_at && (
                      <> · {new Date(invoiceInfo.invoice_raised_at).toLocaleString('en-IE')}</>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-amber-900 mr-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <div className="font-semibold">Invoice pending in AccountsIQ</div>
                  <div className="text-xs text-amber-800/80">Or, if you keyed the invoice yourself, paste the number here to close the loop.</div>
                </div>
              </div>
              <div className="flex items-end gap-2 ml-auto">
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground block mb-1">AccountsIQ Invoice #</label>
                  <Input
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="INV-2026-0341"
                    className="w-44"
                    data-testid="invoice-number-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleMarkInvoiced(); }}
                  />
                </div>
                <Button onClick={handleMarkInvoiced} disabled={savingInvoice || !invoiceNumber.trim()} data-testid="btn-mark-invoiced">
                  <Receipt className="w-4 h-4 mr-1.5" />
                  {savingInvoice ? 'Saving…' : 'Mark Invoiced'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div ref={printRef} className="border rounded-md overflow-x-auto" data-testid="accountant-view-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100 text-xs uppercase text-slate-700">
                <th className="px-3 py-2 text-left">Customer Code</th>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-left">Packaging</th>
                <th className="px-3 py-2 text-right">Units</th>
                <th className="px-3 py-2 text-right">Cases</th>
                <th className="px-3 py-2 text-right">Net kg</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground text-sm">
                    No dispatch lines available yet.
                  </td>
                </tr>
              ) : rows.map((r, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">{r.customer_code || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2">{r.product_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.product_sku || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="text-[10px]">{r.packaging}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmtN(r.units, 0)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtN(r.cases, 1)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtN(r.net_weight, 2)}</td>
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300">
                  <td colSpan={4} className="px-3 py-2 text-sm">TOTAL</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtN(totals.units, 0)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtN(totals.cases, 1)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtN(totals.net_weight, 2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button
            variant="outline"
            onClick={printContent}
            disabled={rows.length === 0}
            data-testid="btn-print-accountant-view"
          >
            <Printer className="w-4 h-4 mr-1.5" /> Print / Save PDF
          </Button>
          <Button
            onClick={copyToClipboard}
            disabled={rows.length === 0}
            data-testid="btn-copy-accountsiq"
          >
            {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
            {copied ? 'Copied' : 'Copy for AccountsIQ'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <InvoicePreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} dispatch={dispatch} onSent={handleAiqSent} />
    </>
  );
}
